import React from "react";
import { motion } from "framer-motion";
import { Star, Anchor, Sparkles, ChevronDown } from "lucide-react";
import { IMAGES, TRUST } from "../data";
import { PrimaryButton, SecondaryButton, openEnquiry } from "./common";

const Hero = () => {
  const go = (href) => document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });

  return (
    <section id="top" data-testid="hero-section" className="relative flex min-h-screen items-center overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0">
        <img
          src={IMAGES.hero.src}
          alt={IMAGES.hero.alt}
          title={IMAGES.hero.title}
          fetchPriority="high"
          className="h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-vivid-base/50 via-vivid-base/70 to-vivid-base" />
        <div className="absolute inset-0 bg-gradient-to-r from-vivid-base/80 via-transparent to-transparent" />
      </div>

      <div className="relative z-10 mx-auto w-full max-w-7xl px-5 pt-28 pb-16 sm:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="max-w-3xl"
        >
          <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-4 py-1.5 backdrop-blur-md">
            <Sparkles className="h-4 w-4 text-vivid-cyan" />
            <span className="text-xs uppercase tracking-[0.2em] text-white/80">Vivid Sydney 2027 · On the Water</span>
          </div>

          <h1 className="font-heading text-5xl font-black leading-[1.02] tracking-tight sm:text-6xl lg:text-[5rem]">
            <span className="text-white">Vivid Cruises Sydney</span>
            <br />
            <span className="text-gradient-vivid">Vivid Sydney Harbour Cruise</span>
          </h1>

          <p className="mt-6 max-w-2xl text-lg leading-relaxed text-white/80">
            Experience <strong className="text-white">Vivid Sydney from the water</strong> with Vivid Cruises Sydney, one of
            Sydney's premier Vivid Sydney Harbour cruises. Enjoy front row views of the illuminated
            Sydney Opera House, Sydney Harbour Bridge, Circular Quay, Barangaroo and the dazzling
            Sydney skyline at night.
          </p>

          <div className="mt-8 flex flex-wrap items-center gap-4">
            <PrimaryButton data-testid="hero-book-now-btn" onClick={() => go("#tickets")}>
              <Anchor className="h-5 w-5" /> Book Your Vivid Cruise
            </PrimaryButton>
            <SecondaryButton data-testid="hero-enquire-btn" onClick={() => openEnquiry("General")}>
              Instant Enquiry
            </SecondaryButton>
          </div>

          <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-3">
            <div className="flex items-center gap-1.5">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="h-4 w-4 fill-vivid-magenta text-vivid-magenta" />
              ))}
              <span className="ml-1 text-sm text-white/80">4.9 · 687+ happy guests</span>
            </div>
            <div className="text-sm text-white/70">
              Adults <span className="font-semibold text-white">$49</span> · Kids <span className="font-semibold text-white">$35</span>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Trust marquee */}
      <div className="absolute bottom-0 left-0 right-0 z-10 overflow-hidden border-t border-white/10 bg-vivid-base/60 py-3 backdrop-blur-md">
        <div className="flex w-max animate-marquee gap-10 whitespace-nowrap">
          {[...TRUST, ...TRUST].map((t, i) => (
            <span key={i} className="flex items-center gap-2 text-sm text-white/60">
              <span className="h-1 w-1 rounded-full bg-vivid-cyan" /> {t}
            </span>
          ))}
        </div>
      </div>

      <button
        onClick={() => go("#why-water")}
        aria-label="Scroll down"
        className="absolute bottom-16 left-1/2 z-10 hidden -translate-x-1/2 animate-bounce text-white/50 sm:block"
      >
        <ChevronDown className="h-7 w-7" />
      </button>
    </section>
  );
};

export default Hero;
